import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  LoadingButton,
  Stack,
  Switch,
  toast,
  Typography,
} from "@altimateai/lego";

import { addDatastore } from "@/helpers/apis";
import { AddDatabasesRequest, AddDatastoreResponse, Datastore as DatastoreType } from "../../types";
import { useTableauConnectionTest } from "../DatastoreAdd/DatastoreConnections/TableauConnection/useTableauConnectionTest";
import TableauProjectPicker from "../DatastoreAdd/DatastoreConnections/TableauProjectPicker";
import { TableauProject } from "../DatastoreAdd/DatastoreConnections/types";
import SourceSitePicker from "./SourceSitePicker";

interface AddTableauCloudSiteDialogProps {
  datastore: DatastoreType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** React-Query key the parent uses for the available-databases query so we
   * can invalidate it on success and refresh the site chips. */
  availableDatabasesQueryKey: readonly unknown[];
}

const AddTableauCloudSiteDialog = ({
  datastore,
  open,
  onOpenChange,
  availableDatabasesQueryKey,
}: AddTableauCloudSiteDialogProps): JSX.Element => {
  const queryClient = useQueryClient();
  // connection_info is stored as "host/site_name" for Tableau.
  const [host = "", currentSiteName = ""] = (datastore.connection_info || "").split("/");
  const url = host ? `https://${host}` : "";

  const [step, setStep] = useState<1 | 2>(1);
  const [siteName, setSiteName] = useState("");
  const [useDifferentPat, setUseDifferentPat] = useState(false);
  const [patName, setPatName] = useState("");
  const [patSecret, setPatSecret] = useState("");
  // Sibling site whose PAT to reuse — backend resolves via (instance_id, name).
  const [sourceSiteName, setSourceSiteName] = useState<string>(currentSiteName);
  // Snapshot of inputs at last Test Connection — when it diverges from the
  // current inputs, Next re-disables so the user re-tests before Finish.
  const [testedInputSignature, setTestedInputSignature] = useState<string | null>(null);

  const { connectionStatus, isTestingConnection, testDatastoreConnection, canPublishWorkbooks } =
    useTableauConnectionTest();

  const currentInputSignature = useDifferentPat
    ? `pat:${siteName}|${patName}|${patSecret}`
    : `nopat:${siteName}|${sourceSiteName}`;

  const resetState = () => {
    setStep(1);
    setSiteName("");
    setUseDifferentPat(false);
    setPatName("");
    setPatSecret("");
    setSourceSiteName(currentSiteName);
    setTestedInputSignature(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  // PAT-source overlay — kept as one object so test/finish stay in sync. When
  // toggle ON, caller-supplied PAT short-circuits the backend's sibling
  // lookup, so source_site_name is irrelevant and omitted.
  const patOverlay = useDifferentPat
    ? { pat_name: patName, pat_secret: patSecret, user: patName, password: patSecret }
    : { source_site_name: sourceSiteName };

  const buildTestPayload = () => ({
    name: datastore.name,
    type: "tableau",
    description: datastore.description || "",
    url,
    host,
    site_name: siteName,
    databases: [siteName],
    datastore_id: datastore.id,
    ...patOverlay,
  });

  const handleTestConnection = () => {
    setTestedInputSignature(currentInputSignature);
    testDatastoreConnection(buildTestPayload());
  };

  const { mutate: createDatastore, isPending: isCreating } = useMutation({
    mutationFn: (data: AddDatabasesRequest) => addDatastore(data),
    onSuccess: (response: AddDatastoreResponse | unknown) => {
      const failure = (Array.isArray(response) ? response : []).find(
        (i: { id?: number | null }) => i?.id == null
      ) as { message?: string } | undefined;
      if (failure) {
        toast({
          variant: "destructive",
          title: "Error",
          description: failure.message || "Failed to add the site.",
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: availableDatabasesQueryKey });
      toast({ title: "Site added", description: `Site '${siteName}' added to ${datastore.name}.` });
      handleOpenChange(false);
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: (err as Error)?.message || "Failed to add the site.",
      });
    },
  });

  const handleFinish = (project: TableauProject) => {
    createDatastore({
      ...datastore,
      databases: [siteName],
      site_name: siteName,
      worksheet_location: project.id,
      url,
      host,
      datastore_id: datastore.id,
      ...patOverlay,
    } as AddDatabasesRequest);
  };

  const step1Valid =
    siteName.trim().length > 0 &&
    (!useDifferentPat || (patName.length > 0 && patSecret.length > 0));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        // Inline style overrides DialogContent's baked-in al-max-w-lg —
        // tailwind-merge in this repo can't dedupe the al- prefix.
        style={{ width: "min(1080px, 95vw)", maxWidth: "min(1080px, 95vw)" }}
        className="al-max-h-[90vh] al-overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Add a site to {datastore.name}</DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <Stack direction="column" className="al-gap-4 al-py-2">
            <Stack direction="column" className="al-gap-1">
              <Label htmlFor="tableau-site-name">Site name</Label>
              <Typography variant="body" size="xs" className="al-text-gray-500">
                The Tableau Cloud site (content URL) you want to add to this connection.
              </Typography>
              <Input
                id="tableau-site-name"
                placeholder="mysite"
                value={siteName}
                onChange={e => setSiteName(e.target.value)}
                autoComplete="off"
              />
            </Stack>

            <SourceSitePicker
              datastoreId={datastore.id}
              value={sourceSiteName}
              onChange={setSourceSiteName}
              disabled={useDifferentPat}
              enabled={open}
            />

            <Stack direction="row" justify="between" align="center" className="al-gap-3">
              <Stack direction="column" className="al-gap-1">
                <Label htmlFor="tableau-use-different-pat">Use a different PAT for this site</Label>
                <Typography variant="body" size="xs" className="al-text-gray-500">
                  When off, the PAT from the site selected above is reused.
                </Typography>
              </Stack>
              <Switch
                id="tableau-use-different-pat"
                checked={useDifferentPat}
                onCheckedChange={setUseDifferentPat}
              />
            </Stack>

            {useDifferentPat && (
              <Stack direction="column" className="al-gap-4">
                <Stack direction="column" className="al-gap-1">
                  <Label htmlFor="tableau-pat-name">Personal Access Token (PAT) Name</Label>
                  <Typography variant="body" size="xs" className="al-text-gray-500">
                    Use a site administrator explorer or site administrator creator PAT.{" "}
                    <Link
                      to="https://docs.altimate.ai/docs/tableau-insights-setup-tableau-cloud#create-an-altimate-service-user"
                      target="_blank"
                      className="al-underline"
                    >
                      Refer to this documentation for details.
                    </Link>
                  </Typography>
                  <Input
                    id="tableau-pat-name"
                    placeholder="altimate_token"
                    value={patName}
                    onChange={e => setPatName(e.target.value)}
                    autoComplete="off"
                  />
                </Stack>
                <Stack direction="column" className="al-gap-1">
                  <Label htmlFor="tableau-pat-secret">Personal Access Token (PAT) Secret</Label>
                  <Input
                    id="tableau-pat-secret"
                    type="password"
                    autoComplete="new-password"
                    placeholder="token_secret_value"
                    value={patSecret}
                    onChange={e => setPatSecret(e.target.value)}
                  />
                </Stack>
              </Stack>
            )}

            <DialogFooter className="al-mt-4">
              <Stack direction="row" justify="between" className="al-w-full">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Stack direction="row" className="al-gap-2">
                  <LoadingButton
                    variant="default"
                    onClick={handleTestConnection}
                    loading={isTestingConnection}
                    disabled={!step1Valid || isTestingConnection}
                  >
                    Test Connection
                  </LoadingButton>
                  <Button
                    variant="default"
                    onClick={() => setStep(2)}
                    disabled={
                      connectionStatus !== "success" ||
                      testedInputSignature !== currentInputSignature
                    }
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
            </DialogFooter>
          </Stack>
        ) : (
          <TableauProjectPicker
            credentials={{
              url,
              site_name: siteName,
              ...(useDifferentPat
                ? { pat_name: patName, pat_secret: patSecret }
                : { datastore_id: datastore.id, source_site_name: sourceSiteName }),
            }}
            canPublishWorkbooks={canPublishWorkbooks ?? true}
            isFinishing={isCreating}
            handleBack={() => setStep(1)}
            handleFinish={handleFinish}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddTableauCloudSiteDialog;
